// The navmesh's input: walkable geometry as one triangle soup, built from shapes the game passes in. The shapes
// mirror what a collision world holds (a heightfield, boxes, cylinders, triangle meshes), so a game builds its
// navmesh from the same data as its colliders (the suggestion "nav graph from level data"), without this package
// importing a physics library. Triangles face up (counter-clockwise seen from above), as Recast expects for floors.

/** [x, y, z] in metres. */
export type NavVec3 = [number, number, number];

/** A rotation as a unit quaternion. */
export interface NavQuat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** One piece of walkable (or blocking) geometry. */
export type NavShape =
  /**
   * A height grid centred on `position`: `rows` cells along z and `cols` cells along x, so (rows + 1) × (cols + 1)
   * samples, row-major from -z (`heights[iz * (cols + 1) + ix]`), `size` the whole extent in x and z, `size[1]`
   * scaling the samples. The same layout as `@rpg/collision`'s heightfield.
   */
  | { kind: "heightfield"; rows: number; cols: number; heights: ArrayLike<number>; size: NavVec3; position?: NavVec3 }
  /** A box: its centre, half extents and rotation. */
  | { kind: "box"; center: NavVec3; halfExtents: NavVec3; rotation?: NavQuat }
  /** An upright cylinder (along y): centre, radius, half height; `segments` around (default 12). */
  | { kind: "cylinder"; center: NavVec3; radius: number; halfHeight: number; segments?: number }
  /** A triangle mesh: xyz per vertex, three indices per triangle, placed at `position` and turned by `rotation`. */
  | { kind: "mesh"; positions: ArrayLike<number>; indices: ArrayLike<number>; position?: NavVec3; rotation?: NavQuat };

/** A triangle soup: xyz per vertex, three indices per triangle. */
export interface NavGeometry {
  positions: Float32Array;
  indices: Uint32Array;
}

const rotate = (q: NavQuat | undefined, x: number, y: number, z: number): NavVec3 => {
  if (!q) return [x, y, z];
  // v' = v + 2w(q×v) + 2 q×(q×v)
  const tx = 2 * (q.y * z - q.z * y);
  const ty = 2 * (q.z * x - q.x * z);
  const tz = 2 * (q.x * y - q.y * x);
  return [x + q.w * tx + (q.y * tz - q.z * ty), y + q.w * ty + (q.z * tx - q.x * tz), z + q.w * tz + (q.x * ty - q.y * tx)];
};

/** Join shapes into one triangle soup for `buildNavWorld` (and `NavWorld.rebuildTile`). */
export function navGeometry(shapes: readonly NavShape[]): NavGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  const vert = (x: number, y: number, z: number): number => {
    pos.push(x, y, z);
    return pos.length / 3 - 1;
  };
  for (const s of shapes) {
    if (s.kind === "heightfield") {
      const [ox, oy, oz] = s.position ?? [0, 0, 0];
      const base = pos.length / 3;
      for (let iz = 0; iz <= s.rows; iz++)
        for (let ix = 0; ix <= s.cols; ix++) {
          const h = s.heights[iz * (s.cols + 1) + ix] ?? 0;
          vert(ox + (ix / s.cols - 0.5) * s.size[0], oy + h * s.size[1], oz + (iz / s.rows - 0.5) * s.size[2]);
        }
      const at = (ix: number, iz: number): number => base + iz * (s.cols + 1) + ix;
      for (let iz = 0; iz < s.rows; iz++)
        for (let ix = 0; ix < s.cols; ix++) {
          const a = at(ix, iz);
          const b = at(ix + 1, iz);
          const c = at(ix, iz + 1);
          const d = at(ix + 1, iz + 1);
          // (x0,z0) (x0,z1) (x1,z0) faces up
          idx.push(a, c, b, b, c, d);
        }
    } else if (s.kind === "box") {
      const [hx, hy, hz] = s.halfExtents;
      const corner = (sx: number, sy: number, sz: number): number => {
        const [x, y, z] = rotate(s.rotation, sx * hx, sy * hy, sz * hz);
        return vert(s.center[0] + x, s.center[1] + y, s.center[2] + z);
      };
      const v = [corner(-1, -1, -1), corner(1, -1, -1), corner(1, -1, 1), corner(-1, -1, 1), corner(-1, 1, -1), corner(1, 1, -1), corner(1, 1, 1), corner(-1, 1, 1)];
      // outward faces, counter-clockwise seen from outside
      const quads = [
        [4, 7, 6, 5], // top
        [0, 1, 2, 3], // bottom
        [0, 4, 5, 1], // -z
        [3, 2, 6, 7], // +z
        [0, 3, 7, 4], // -x
        [1, 5, 6, 2], // +x
      ];
      for (const [a, b, c, d] of quads) idx.push(v[a!]!, v[b!]!, v[c!]!, v[a!]!, v[c!]!, v[d!]!);
    } else if (s.kind === "cylinder") {
      const n = Math.max(3, s.segments ?? 12);
      const [cx, cy, cz] = s.center;
      const top = vert(cx, cy + s.halfHeight, cz);
      const bot = vert(cx, cy - s.halfHeight, cz);
      const ring: [number, number][] = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const x = cx + Math.cos(a) * s.radius;
        const z = cz + Math.sin(a) * s.radius;
        ring.push([vert(x, cy + s.halfHeight, z), vert(x, cy - s.halfHeight, z)]);
      }
      for (let i = 0; i < n; i++) {
        const [t0, b0] = ring[i]!;
        const [t1, b1] = ring[(i + 1) % n]!;
        idx.push(top, t1, t0, bot, b0, b1, t0, t1, b1, t0, b1, b0);
      }
    } else {
      const base = pos.length / 3;
      const [ox, oy, oz] = s.position ?? [0, 0, 0];
      for (let i = 0; i + 2 < s.positions.length; i += 3) {
        const [x, y, z] = rotate(s.rotation, s.positions[i]!, s.positions[i + 1]!, s.positions[i + 2]!);
        vert(ox + x, oy + y, oz + z);
      }
      for (let i = 0; i < s.indices.length; i++) idx.push(base + s.indices[i]!);
    }
  }
  return { positions: Float32Array.from(pos), indices: Uint32Array.from(idx) };
}

/** The geometry's bounds: [min, max]. */
export function geometryBounds(g: NavGeometry): [NavVec3, NavVec3] {
  const min: NavVec3 = [Infinity, Infinity, Infinity];
  const max: NavVec3 = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < g.positions.length; i += 3)
    for (let k = 0; k < 3; k++) {
      const v = g.positions[i + k]!;
      if (v < min[k]!) min[k] = v;
      if (v > max[k]!) max[k] = v;
    }
  return [min, max];
}
